import { useEffect, useRef, useState } from "react";
import { ACCENT_PRESETS, useTheme } from "../theme/ThemeContext";
import type { Appearance, Density, TextSize } from "../theme/ThemeContext";
import { NavIcon } from "./NavIcon";

const APPEARANCE_OPTIONS: { value: Appearance; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "dim", label: "Dim" },
  { value: "contrast", label: "Contrast" },
];

const DENSITY_OPTIONS: { value: Density; label: string }[] = [
  { value: "comfortable", label: "Comfortable" },
  { value: "compact", label: "Compact" },
];

const TEXT_SIZE_OPTIONS: { value: TextSize; label: string }[] = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];

export function AppearanceMenu() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="appearance-menu" ref={containerRef}>
      <button
        type="button"
        className="appearance-menu-trigger"
        onClick={() => setOpen((v) => !v)}
        title="Appearance settings"
        aria-label="Appearance settings"
      >
        <NavIcon name="gear" size={16} />
      </button>

      {open && (
        <div className="appearance-menu-popover">
          <div className="appearance-menu-section-label">Appearance</div>
          <div className="appearance-menu-pill-row">
            {APPEARANCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={theme.appearance === opt.value ? "appearance-pill active" : "appearance-pill"}
                onClick={() => theme.setAppearance(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="appearance-menu-section-label">Accent color</div>
          <div className="appearance-menu-swatch-row">
            {ACCENT_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                className={
                  theme.accent === preset
                    ? "appearance-swatch appearance-swatch-active"
                    : "appearance-swatch"
                }
                style={{ background: `var(--accent-preset-${preset})` }}
                onClick={() => theme.setAccent(preset)}
                title={preset}
                aria-label={`Accent color ${preset}`}
              />
            ))}
            <label
              className={
                theme.accent === "custom"
                  ? "appearance-swatch appearance-swatch-custom appearance-swatch-active"
                  : "appearance-swatch appearance-swatch-custom"
              }
              title="Custom color"
            >
              +
              <input
                type="color"
                value={theme.customAccentHex ?? "#4f46e5"}
                onChange={(e) => theme.setCustomAccentHex(e.target.value)}
              />
            </label>
          </div>

          <div className="appearance-menu-section-label">Density</div>
          <p className="help-text" style={{ margin: "0 0 8px" }}>
            Compact tightens table rows and panel padding — more rows on screen for large
            datasets.
          </p>
          <div className="appearance-menu-pill-row">
            {DENSITY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={theme.density === opt.value ? "appearance-pill active" : "appearance-pill"}
                onClick={() => theme.setDensity(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="appearance-menu-section-label">Text size</div>
          <div className="appearance-menu-pill-row">
            {TEXT_SIZE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={theme.textSize === opt.value ? "appearance-pill active" : "appearance-pill"}
                onClick={() => theme.setTextSize(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
